import React, { useState } from "react";
import Plot from "react-plotly.js";

import { AddFilterBaseFunc, chartAttrs, defaultConfig, defaultStyles, graphColors, IFilterKeys, IFilters, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { ReviewsByPeriodHierarchy, PatientsByPeriodHierarchy, DiagnosesByPeriodHierarchy } from "models/mdt/plotdata";
import { PlotData, PlotType } from "plotly.js";
import { Dialog, Grid } from "@mui/material";
import { addHierarchyFilterFactory } from "helpers/filterHelper";
import { HierarchyBreadcrumb } from "components/common/HierarchyBreadcrumb";
import { DashTable } from "components/common/DashTable";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { chartDimesionsCalc, ChartOrientation, ChartType, QoolDashChart } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";

interface IProps {
	plotDataReviews: ReviewsByPeriodHierarchy.IHierarchyPlotData;
	plotDataPatients: PatientsByPeriodHierarchy.IHierarchyPlotData;
	plotDataDiagnoses: DiagnosesByPeriodHierarchy.IHierarchyPlotData;
	activeFilters: IFilters<IFilterKeys>;
	//addFilterFn: (e: any, filterKey: string, getY: (p) => string) => void;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
	removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
	noProfileCount: number;
}

/**
 * Reviews by service chart / My MDTs
 */
export function MDTActivityTrendChart(props: IProps) {
	const { plotDataReviews, plotDataPatients, plotDataDiagnoses, activeFilters, addFilterFn, removeFilterFn } = props;
	const hierarchy = plotDataReviews;

	let valuesROriginal = [];
	let labelsROriginal = [];
	let valuesPOriginal = [];
	let labelsPOriginal = [];
	let valuesDOriginal = [];
	let labelsDOriginal = [];

	let valuesRReversed = [];
	let labelsRReversed = [];
	let valuesPReversed = [];
	let labelsPReversed = [];
	let valuesDReversed = [];
	let labelsDReversed = [];

	let Reviews = {};
	let Patients = {};
	let Diagnoses = {};

	
	valuesROriginal = hierarchy.values;
	labelsROriginal = hierarchy.labels;
	valuesRReversed = hierarchy.values.slice().reverse();
	labelsRReversed = hierarchy.labels.slice().reverse();
	Reviews = { "name": "Presentations", "labels": labelsROriginal, "labelsReversed": labelsRReversed, "values": valuesROriginal, "valuesReversed": valuesRReversed };

	valuesPOriginal = plotDataPatients.values;
	labelsPOriginal = plotDataPatients.labels;
	valuesPReversed = plotDataPatients.values.slice().reverse();
	labelsPReversed = plotDataPatients.labels.slice().reverse();
	Patients = { "name": "Patients", "labels": labelsPOriginal, "labelsReversed": labelsPReversed, "values": valuesPOriginal, "valuesReversed": valuesPReversed };

	valuesDOriginal = plotDataDiagnoses.values;
	labelsDOriginal = plotDataDiagnoses.labels;
	valuesDReversed = plotDataDiagnoses.values.slice().reverse();
	labelsDReversed = plotDataDiagnoses.labels.slice().reverse();
	Diagnoses = { "name": "Diagnoses", "labels": labelsDOriginal, "labelsReversed": labelsDReversed, "values": valuesDOriginal, "valuesReversed": valuesDReversed };

	const tracesArray: any[] = [];

	tracesArray.push(Patients);
	tracesArray.push(Diagnoses);
	tracesArray.push(Reviews);	


	//Create Summary box
	const presentations = valuesROriginal.reduce((a, b) => a + b, 0);
	const diagnoses = valuesDOriginal.reduce((a, b) => a + b, 0);
	const patients = valuesPOriginal.reduce((a, b) => a + b, 0);
	const representationRate = ((presentations - diagnoses) / diagnoses * 100).toFixed(1);

	const box = (): JSX.Element => {
		return (
			<Grid container spacing={2}>
				<Grid item xs={3} >
					{patients.toLocaleString()}
					<br />
					{diagnoses.toLocaleString()}
					<br />
					{presentations.toLocaleString()}
					<br />
					{representationRate}%
					<br />
					{props.noProfileCount}
				</Grid>
				<Grid item xs={9}  >
					Patients
					<br />
					Diagnoses
					<br />
					Presentations
					<br />
					Representation rate
					<br />
					Records excluded from data analysis
				</Grid>
			</Grid>
		)
	}

	//Text for info icon on hover
	const toolTipText = ["Patients:", "A unique count of a patient presented to a MDT",
		"Diagnoses:", "A unique count of a patient's diagnoses presented to a MDT",
		"Presentations:", "A count of patient presentations to MDT where a patient can present multiple times",
		"Representation rate:", "The rate at which a patient is presented to an individual MDT more than once for a given diagnosis. n=Patients presented to MDT, N=Presentations to MDT",
		"", "Records excluded from analysis are those in which a diagnosis has not been selected at time of referral"
	];


	const legend = (chartType: ChartType): JSX.Element => {

		if (chartType == ChartType.Table)
			return null;


		return (
			<div className="dash-legend">
				<div>
					<div className="key" style={{ backgroundColor: graphColors.default[0] }}></div>
					<div className="title">Patients</div>
				</div>
				<div>
					<div className="key" style={{ backgroundColor: graphColors.default[1] }}></div>
					<div className="title">Diagnoses</div>
				</div>
				<div>
					<div className="key" style={{ backgroundColor: graphColors.default[2] }}></div>
					<div className="title">Presentations</div>
				</div>
				
			</div>

		)
	}

	const filterFn = (chartType: ChartType, chartOrientation: ChartOrientation) => 
		addHierarchyFilterFactory(props.addFilterFn, DashboardFilterKeys.Year.key, hierarchy, (chartType == ChartType.Pie ? p => p.data.labels[p.pointNumber].toString() : (chartOrientation == ChartOrientation.Horizontal ? p => p.y.toString() : p => p.x.toString()))); 


	const breadcrumb = (
		<HierarchyBreadcrumb data={hierarchy} filterKey={DashboardFilterKeys.Year.key} addFilter={addFilterFn} removeFilter={removeFilterFn} />
		);


	return (
		<QoolDashChart
			xAxis={{ horizontalOrientation: { label: "" }, verticalOrientation: { label: "" } }}
			yAxis={{ horizontalOrientation: { label: "" }, verticalOrientation: { label: "" } }}
			chartOrder={{ horizontalOrientation: { reversed: false }, verticalOrientation: { reversed: false } }}
			defaultChartOrientation={ChartOrientation.Vertical}
			defaultChartType={ChartType.Line}
			chartTitle="My MDT Activity Trend"
			filterKey={""}
			plotData={tracesArray}
			calculateRangeOnPlotDataIndex={2}
			activeFilters={props.activeFilters}
			addFilterFn={props.addFilterFn}
			removeFilterFn={props.removeFilterFn}
			filterFn={filterFn}
			hoverText="%{fullData.name}"
			textFn={(v) => v.map(String)} 
			breadcrumb={breadcrumb}
			chartIcons={{
				pie: false,
				verticalBar: false,
				horizontalBar: false,
				help: toolTipText
			}}
			chartDimensions={{
				chartHeightProportion: 50,
				parentColNumber: 12,
				tabColNumber: 4
			}}
			summary={box()}
			legend={legend}
		/>
	)
}
